import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAddSecondaryPhone } from "@/features/auth/hooks/use-phone-challenge";
import { useUser } from "@/features/auth/hooks/use-user";
import type { OnboardingDraft } from "@/features/onboarding/types";
import { useTranslation } from "@/features/utils/i18n";
import { PhoneChallenge } from "@/views/login/challenges/phone";
import { CheckCircleIcon, DeviceMobileIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { OnboardingLayout } from "./layout";

/**
 * The client's own mobile, verified.
 *
 * This is the one thing in the wizard that is not a preference. The number
 * becomes the demo connector's caller number, and caller numbers are globally
 * unique across tenants: calls from it will reach *this* tenant's agent. An
 * unverified number would mean claiming a stranger's line, so the code is not
 * ceremony — the server refuses to finish without it.
 *
 * The phone/OTP round-trip itself is `PhoneChallenge`, the same widget the
 * login and the account page use. It hands back a token rather than a number,
 * which is exactly right here: the server decodes it and writes the number
 * itself, so the client never gets to assert which number was verified.
 *
 * A client whose account already carries a proven number picks it in one
 * click: re-OTPing a number we verified last week buys nothing and costs an
 * SMS. The number typed at sign-up is not one of them — it is pre-filled in the
 * code form instead, one tap from being proven.
 */
export function StepMobile({
  draft,
  onVerified,
  onChangeNumber,
  onNext,
  onBack,
  isSubmitting,
  analysisPending,
}: {
  draft: OnboardingDraft;
  /**
   * `phoneChallengeToken` is absent when the client picked a number already on
   * their account — the server verifies that against the account instead.
   */
  onVerified: (phone: string, phoneChallengeToken?: string) => void;
  /** Clears the current selection so the client can use a different number. */
  onChangeNumber: () => void;
  onNext: () => void;
  onBack: () => void;
  isSubmitting?: boolean;
  analysisPending?: boolean;
}) {
  const { t } = useTranslation("onboarding");
  const { data: user } = useUser();
  const addSecondaryPhone = useAddSecondaryPhone();

  const verified = draft.contact?.mobile_verified
    ? draft.contact.mobile_phone
    : undefined;

  // Numbers the account has already proven: a code reached them, so picking
  // one is a single click. The primary only once `phone_verified` — the number
  // typed at sign-up is not one of them, whatever it says on the account.
  const knownPhones = [
    user?.phone_verified === true ? user.phone : null,
    ...(user?.additional_phones || []),
  ].filter(Boolean) as string[];

  // The number the client is most likely to confirm: the one already on the
  // draft, else the one typed at sign-up. Pre-filled, still editable, and
  // proven like any other by the code.
  const suggestedPhone =
    draft.contact?.mobile_phone || user?.phone || undefined;

  const [useKnown, setUseKnown] = useState(knownPhones.length > 0);

  const handleChallengeSuccess = (token: string) => {
    // Attach it to the account too, best-effort: the wizard's job is the draft,
    // and a duplicate-phone error here must not block the step.
    addSecondaryPhone.mutate(
      { phoneChallengeToken: token },
      { onError: () => undefined },
    );
    // The number itself comes back from the server, decoded from the token.
    onVerified("", token);
  };

  const body = () => {
    if (verified) {
      return (
        <div className="flex items-center gap-3 rounded-lg border-2 border-primary/30 bg-primary/5 p-4">
          <CheckCircleIcon
            className="h-5 w-5 shrink-0 text-primary"
            weight="fill"
          />
          <div className="flex-1">
            <p className="font-medium">
              {formatPhoneNumberIntl(verified) || verified}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("steps.mobile.verified")}
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setUseKnown(false);
              onChangeNumber();
            }}
          >
            {t("steps.mobile.change")}
          </Button>
        </div>
      );
    }

    if (useKnown) {
      return (
        <div className="space-y-2">
          <Label>{t("steps.mobile.pickKnown")}</Label>
          <div className="divide-y rounded-lg border">
            {knownPhones.map((phone) => (
              <button
                key={phone}
                type="button"
                onClick={() => onVerified(phone)}
                className="flex w-full items-center gap-3 p-4 text-left hover:bg-slate-500/5"
              >
                <DeviceMobileIcon
                  className="h-5 w-5 shrink-0 text-muted-foreground"
                  weight="fill"
                />
                <span className="flex-1 font-medium">
                  {formatPhoneNumberIntl(phone) || phone}
                </span>
                <span className="text-sm text-muted-foreground">
                  {t("steps.mobile.use")}
                </span>
              </button>
            ))}
          </div>
          <Button
            type="button"
            variant="link"
            className="h-auto p-0"
            onClick={() => setUseKnown(false)}
          >
            {t("steps.mobile.useAnother")}
          </Button>
        </div>
      );
    }

    return (
      <div className="-mx-6">
        <PhoneChallenge
          initialPhone={suggestedPhone}
          onSuccess={handleChallengeSuccess}
        />
        {knownPhones.length > 0 && (
          <div className="px-6">
            <Button
              type="button"
              variant="link"
              className="h-auto p-0"
              onClick={() => setUseKnown(true)}
            >
              {t("steps.mobile.backToKnown")}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <OnboardingLayout
      step="mobile"
      title={t("steps.mobile.title")}
      intro={t("steps.mobile.intro")}
      onBack={onBack}
      onNext={onNext}
      nextDisabled={!verified}
      isSubmitting={isSubmitting}
      analysisPending={analysisPending}
    >
      {body()}
    </OnboardingLayout>
  );
}
